import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSession } from './useSession.ts';
import { Profile, CreateSubAccountData, CreateSubAccountResponse, UpdateSubAccountData } from '../types/subaccounts.ts';

export const useSubAccounts = (searchQuery?: string) => {
  return useQuery({
    queryKey: ['sub-accounts', searchQuery],
    queryFn: async (): Promise<Profile[]> => {
      const session = await getSession();

      // Build URL with search parameter
      const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sub-accounts`);
      if (searchQuery && searchQuery.trim()) {
        url.searchParams.set('search', searchQuery.trim());
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to fetch sub-accounts: ${errorData}`);
      }

      const result = await response.json();
      return result.data || [];
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};


export const useCreateSubAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSubAccountData): Promise<CreateSubAccountResponse> => {
      const session = await getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sub-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          name: data.full_name,
          plan: data.plan_identifier,
          password: data.password,
          credits: data.free_credits,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create sub-account');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-accounts'] });
    },
  });
};

export const useUpdateSubAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateSubAccountData)=> {
      const session = await getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sub-accounts`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: data.user_id,
          name: data.name,
          plan: data.plan,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update sub-account');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-accounts'] });
    },
  });
};


export const useDeleteSubAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string): Promise<{ success: boolean }> => {
      const session = await getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sub-accounts`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete sub-account');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-accounts'] });
    },
  });
};

export const useRestoreSubAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string): Promise<{ success: boolean }> => {
      const session = await getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sub-accounts?action=restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to restore sub-account');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-accounts'] });
    },
  });
};

export const usePermanentDeleteSubAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string): Promise<{ success: boolean }> => {
      const session = await getSession();

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sub-accounts?action=permanent`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to permanently delete sub-account');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-accounts'] });
    },
  });
};